import { useEffect } from 'react';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { DiscountPriceView } from '@/app/components/ppra/components/ppra-payment-page/components/MembershipSummary';

type ModalWinbackOfferProps = {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  originalPriceCents: number;
  discountCents: number;
  finalPriceCents: number;
  isSubmitting?: boolean;
};

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function ModalWinbackOffer({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  originalPriceCents,
  discountCents,
  finalPriceCents,
  isSubmitting = false,
}: ModalWinbackOfferProps) {
  const { callModalViewed } = useAnalyticsCalls();

  useEffect(() => {
    if (isOpen) {
      callModalViewed('Winback Offer', {
        original_price_cents: originalPriceCents.toString(),
        discount_cents: discountCents.toString(),
        final_price_cents: finalPriceCents.toString(),
      });
    }
  }, [isOpen, originalPriceCents, discountCents, finalPriceCents, callModalViewed]);

  if (!isOpen) return null;

  return (
    <div className="dashboard-reset">
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="relative w-full max-w-[600px] rounded-lg border border-solid border-[#e2e8f0] bg-white p-6 shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)] sm:p-10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl leading-7 font-semibold tracking-[0.208px] text-slate-800 sm:text-4xl">
              Thanks for giving Catch a try. Before you go...
            </h2>
          </div>

          <p className="mb-6 text-sm leading-5 tracking-[0.208px] text-slate-600 sm:text-base sm:leading-6">
            Your risk changes over time. Canceling now means you won't be alerted to those changes.
          </p>

          <p className="mb-6 text-sm leading-5 tracking-[0.208px] text-slate-600  sm:text-base sm:leading-6">
            Since you've already begun your journey, we're offering you a discounted rate to continue.
          </p>

          <div className="border-2 border-solid rounded-md border-slate-300 m-6 p-4">
             <DiscountPriceView yearSuffix={'/yr'} productName={'Catch Membership'} originalPrice={originalPriceCents/100} discountedPrice={finalPriceCents/100} savingsAmount={discountCents/100} />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse gap-3 md:flex-row">
            <button
              className="flex h-12 flex-1 items-center justify-center rounded-full bg-[#183344] px-5 py-2.5 text-sm leading-[1.4] font-bold text-white transition-colors hover:bg-[#2a4859] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onAccept}
              disabled={isSubmitting}
              data-testid="accept-winback-button"
            >
              {isSubmitting ? 'Processing...' : 'Claim Discount'}
            </button>
            <button
              className="flex h-12 flex-1 items-center justify-center rounded-full border border-solid border-[#183344] bg-white px-5 py-2.5 text-sm leading-[1.4] font-bold text-[#183344] transition-colors hover:bg-[#f7fafc] disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onDecline}
              disabled={isSubmitting}
              data-testid="decline-winback-button"
            >
              Cancel My Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
